// pages/SAPCompanyPanel.jsx — company-code drill-down for the Process Map.
// Shows the FICO profile of one company code (org assignment, validations,
// substitution, CO-PA record mix, volumes, bank document types) and flags every
// attribute whose value differs from the most common value in its landscape.
// Data: sapCompanyData.js (static snapshot, 29-10-2026). Bilingual ID/EN.
import { useState } from 'react';
import { ATTR_LABELS, VOL_LABELS, COPA_TYPES } from './sapCompanyData';
import { PLANTS, CC_PC, KOSAR, CO_STRUCT } from './sapPlantCoData';

const PT = {
  en: { plants: 'Plants & their link to CO', plant: 'Plant', cal: 'Calendar', maint: 'Maint. plant', purch: 'Purch. org', sloc: 'Stor. loc.',
        wc: 'Work centers → cost centers', matPc: 'Materials → profit center', price: 'Price control S / V',
        wcNone: 'no work centers', defaultPc: 'on default PC', ccpc: 'Cost centers & profit centers', ccTotal: 'cost centers',
        allPc: 'all carry a profit center', pcN: 'profit centers assigned to this company', struct: 'Plant & CO structure', show: 'Show', hide: 'Hide', kokrs: 'Controlling area' },
  id: { plants: 'Plant & kaitannya ke CO', plant: 'Plant', cal: 'Kalender', maint: 'Maint. plant', purch: 'Purch. org', sloc: 'Stor. loc.',
        wc: 'Work center → cost center', matPc: 'Material → profit center', price: 'Price control S / V',
        wcNone: 'tanpa work center', defaultPc: 'di PC default', ccpc: 'Cost center & profit center', ccTotal: 'cost center',
        allPc: 'semuanya punya profit center', pcN: 'profit center ter-assign ke company ini', struct: 'Plant & struktur CO', show: 'Buka', hide: 'Tutup', kokrs: 'Controlling area' },
};

// Landscape-level card: how plants, cost centers and profit centers are set up.
export function COStructureCard({ landKey, lang, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const s = CO_STRUCT[landKey];
  if (!s) return null;
  const L = lang === 'id' ? 'id' : 'en';
  const t = PT[L];
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col gap-2">
      <button type="button" onClick={() => setOpen(o => !o)} className="flex items-center gap-3 text-left w-full">
        <span className="text-[14px] font-bold text-slate-800">{t.struct}</span>
        <span className="font-mono text-[11.5px] text-slate-400 flex-1 min-w-0 truncate">{t.kokrs}: {s.kokrs}</span>
        <span className="text-[12px] font-semibold text-brand-700 shrink-0">{open ? `▲ ${t.hide}` : `▼ ${t.show}`}</span>
      </button>
      {open && (
        <ul className="list-disc pl-4 text-[12.5px] text-slate-600 space-y-1">
          {s.facts.map((f, i) => <li key={i}>{f[L]}</li>)}
        </ul>
      )}
    </div>
  );
}

const T = {
  en: { specific: 'What is specific to this company', profile: 'FICO profile', differs: 'differs', differsTip: 'Different from the other company codes in this landscape',
        copa: 'CO-PA line items by record type (Sep 2026)', noCopa: 'No CO-PA: this controlling area has no operating concern assigned.',
        volumes: 'Volumes', banks: 'Bank & cash document types (Sep 2026)', subst: 'Substitution steps', none: 'No substitution active',
        close: 'Close', compareHint: 'Highlighted rows differ from the majority of company codes in this landscape.', total: 'Total' },
  id: { specific: 'Yang khas untuk company ini', profile: 'Profil FICO', differs: 'berbeda', differsTip: 'Berbeda dengan company code lain di landscape ini',
        copa: 'Line item CO-PA per record type (Sep 2026)', noCopa: 'Tidak ada CO-PA: controlling area ini tidak punya operating concern.',
        volumes: 'Volume', banks: 'Tipe dokumen bank & kas (Sep 2026)', subst: 'Langkah substitusi', none: 'Tidak ada substitusi aktif',
        close: 'Tutup', compareHint: 'Baris yang ditandai berbeda dengan mayoritas company code di landscape ini.', total: 'Total' },
};

// Most common value per attribute across the landscape's company codes.
function majority(companies, key) {
  const m = {};
  companies.forEach(c => { const v = c.attrs[key]; m[v] = (m[v] || 0) + 1; });
  let best = null, n = 0;
  Object.entries(m).forEach(([v, k]) => { if (k > n) { best = v; n = k; } });
  // No majority when every company has its own value: nothing is "different".
  return n > 1 ? best : null;
}

export default function SAPCompanyPanel({ company, companies, lang, onClose }) {
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];
  const fmt = n => n.toLocaleString(L === 'id' ? 'id-ID' : 'en-US');
  const copaTotal = company.copa ? Object.values(company.copa).reduce((a, b) => a + b, 0) : 0;
  const bankMax = company.banks.length ? Math.max(...company.banks.map(b => b[1])) : 0;

  return (
    <div className="bg-white border-2 border-brand-300 rounded-xl px-5 py-4 flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono text-[20px] font-extrabold text-brand-700">{company.code}</span>
            <span className="text-[16px] font-bold text-slate-800">{company.name}</span>
            <span className="text-[12px] font-mono text-slate-400">{company.short}</span>
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">{t.compareHint}</div>
        </div>
        <button type="button" onClick={onClose}
          className="px-3 py-1 text-[12px] font-semibold rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">✕ {t.close}</button>
      </div>

      {/* Highlights */}
      <div className="rounded-lg bg-brand-50 border border-brand-100 px-4 py-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-brand-700 mb-1.5">{t.specific}</div>
        <ul className="list-disc pl-4 text-[13px] text-slate-700 space-y-1">
          {company.hi.map((h, i) => <li key={i}>{h[L]}</li>)}
        </ul>
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {/* Profile table */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.profile}</div>
          <table className="w-full text-[12.5px]">
            <tbody>
              {Object.keys(ATTR_LABELS).filter(k => company.attrs[k] != null).map(k => {
                const maj = majority(companies, k);
                const diff = companies.length > 1 && maj !== null && company.attrs[k] !== maj;
                return (
                  <tr key={k} className={`border-b border-slate-100 ${diff ? 'bg-amber-50' : ''}`}>
                    <td className="py-1 pr-3 text-slate-500 align-top whitespace-nowrap">{ATTR_LABELS[k][L]}</td>
                    <td className="py-1 font-mono text-slate-800 align-top">
                      {company.attrs[k]}
                      {diff && (
                        <span title={t.differsTip}
                          className="ml-2 font-sans text-[10px] font-bold px-1.5 py-0.5 rounded border border-amber-400 text-amber-700 whitespace-nowrap">
                          {t.differs}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-4 min-w-0">
          {/* CO-PA */}
          <div className="flex flex-col gap-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.copa}</div>
            {company.copa ? (
              <>
                <div className="flex h-3 rounded overflow-hidden bg-slate-100" aria-hidden="true">
                  {['F', 'B', 'C', 'D'].map((k, i) => (
                    <div key={k} style={{ width: `${(company.copa[k] / copaTotal) * 100}%`, background: ['#b23a4a', '#11766f', '#4b55b8', '#b07812'][i] }} />
                  ))}
                </div>
                {['F', 'B', 'C', 'D'].map((k, i) => (
                  <div key={k} className="flex items-center gap-2 text-[12.5px]">
                    <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: ['#b23a4a', '#11766f', '#4b55b8', '#b07812'][i] }} />
                    <span className="flex-1 text-slate-600">{COPA_TYPES[k][L]}</span>
                    <span className="font-mono tabular-nums text-slate-800">{fmt(company.copa[k])}</span>
                    <span className="font-mono tabular-nums text-slate-400 w-12 text-right">{((company.copa[k] / copaTotal) * 100).toFixed(1)}%</span>
                  </div>
                ))}
                <div className="flex justify-between text-[12px] text-slate-500 border-t border-slate-100 pt-1">
                  <span>{t.total}</span><span className="font-mono tabular-nums">{fmt(copaTotal)}</span>
                </div>
              </>
            ) : <p className="text-[12.5px] text-slate-500 m-0">{t.noCopa}</p>}
          </div>

          {/* Substitution */}
          <div className="flex flex-col gap-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.subst}{company.subst ? ` · ${company.subst.id}` : ''}</div>
            {company.subst
              ? <ol className="list-decimal pl-5 text-[12.5px] text-slate-600 space-y-0.5">{company.subst.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
              : <p className="text-[12.5px] text-slate-500 m-0">{t.none}</p>}
          </div>
        </div>
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {/* Volumes */}
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.volumes}</div>
          <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
            {Object.keys(VOL_LABELS).filter(k => company.vol[k] != null).map(k => (
              <div key={k} className="border border-slate-200 rounded-lg px-2.5 py-1.5">
                <div className="font-mono text-[15px] font-bold text-slate-800 tabular-nums">{fmt(company.vol[k])}</div>
                <div className="text-[11px] text-slate-500 leading-tight">{VOL_LABELS[k][L]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bank doc types */}
        {company.banks.length > 0 && (
          <div className="flex flex-col gap-1.5 min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.banks}</div>
            {company.banks.map(([label, n]) => (
              <div key={label} className="grid items-center gap-2 text-[12.5px]" style={{ gridTemplateColumns: 'minmax(0,1fr) 56px' }}>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="truncate text-slate-700" title={label}>{label}</span>
                  <div className="h-1.5 bg-slate-100 rounded overflow-hidden"><div className="h-full bg-brand-500" style={{ width: `${(n / bankMax) * 100}%` }} /></div>
                </div>
                <span className="font-mono text-right tabular-nums text-slate-700">{fmt(n)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <PlantsAndCo code={company.code} L={L} fmt={fmt} />
    </div>
  );
}

// Plants of the company with their CO links, plus cost/profit center makeup.
function PlantsAndCo({ code, L, fmt }) {
  const t = PT[L];
  const plants = PLANTS[code] || [];
  const ccpc = CC_PC[code];
  const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
  const ccTotal = ccpc ? Object.values(ccpc.cc).reduce((a, b) => a + b, 0) : 0;
  const catColor = { D: '#4b55b8', I: '#11766f', G: '#b07812', H: '#a35a22', V: '#b23a4a', W: '#2f7d32' };
  return (
    <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
      {plants.length > 0 && (
        <div className="flex flex-col gap-1.5 min-w-0" style={{ gridColumn: '1 / -1' }}>
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.plants}</div>
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-[12px] min-w-[760px]">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-left">
                  <th className="px-2.5 py-1.5 font-semibold">{t.plant}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.cal}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.maint}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.purch}</th>
                  <th className="px-2.5 py-1.5 font-semibold text-right">{t.sloc}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.wc}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.matPc}</th>
                  <th className="px-2.5 py-1.5 font-semibold">{t.price}</th>
                </tr>
              </thead>
              <tbody>
                {plants.map(p => (
                  <tr key={p.werks} className="border-t border-slate-100 align-top">
                    <td className="px-2.5 py-1.5"><b className="font-mono text-brand-700">{p.werks}</b> <span className="text-slate-500">{p.name}</span></td>
                    <td className="px-2.5 py-1.5 font-mono">{p.cal}</td>
                    <td className="px-2.5 py-1.5 font-mono">{p.maint}</td>
                    <td className="px-2.5 py-1.5 font-mono">{p.purch}</td>
                    <td className="px-2.5 py-1.5 font-mono text-right tabular-nums">{fmt(p.sloc)}</td>
                    <td className="px-2.5 py-1.5">
                      {p.wc == null ? <span className="text-slate-400">—</span>
                        : p.wc === 0 ? <span className="text-slate-400">{t.wcNone}</span>
                        : <span className="font-mono tabular-nums">{fmt(p.wcCC)}/{fmt(p.wc)} → {fmt(p.ccN)} CC</span>}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {p.mat == null ? <span className="text-slate-400">—</span> : (
                        <span className="font-mono tabular-nums">
                          {fmt(p.mat)} mat · {p.pcN} PC
                          <span className="block text-[11px] text-slate-500 font-sans">{p.pcTop[0]}: {pct(p.pcTop[1], p.mat)}% {t.defaultPc}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5">
                      {p.S == null ? <span className="text-slate-400">—</span> : (
                        <span className="font-mono tabular-nums">{pct(p.S, p.S + p.V)}% S · {pct(p.V, p.S + p.V)}% V</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {ccpc && (
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.ccpc}</div>
          <div className="flex h-3 rounded overflow-hidden bg-slate-100" aria-hidden="true">
            {Object.entries(ccpc.cc).map(([k, n]) => <div key={k} style={{ width: `${(n / ccTotal) * 100}%`, background: catColor[k] || '#94a3b8' }} />)}
          </div>
          {Object.entries(ccpc.cc).map(([k, n]) => (
            <div key={k} className="flex items-center gap-2 text-[12.5px]">
              <i className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: catColor[k] || '#94a3b8' }} />
              <span className="flex-1 text-slate-600"><b className="font-mono">{k}</b> · {KOSAR[k] ? KOSAR[k][L] : k}</span>
              <span className="font-mono tabular-nums text-slate-800">{fmt(n)}</span>
            </div>
          ))}
          <div className="text-[12px] text-slate-500 border-t border-slate-100 pt-1">
            {fmt(ccTotal)} {t.ccTotal} · {ccpc.ccWithPc === ccTotal ? t.allPc : `${fmt(ccpc.ccWithPc)} PC`}
          </div>
        </div>
      )}
    </div>
  );
}
